import { Checkbox, Input, Loader, Modal, Switch, TextInput, Textarea } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    SupportedEventTypesResponse,
    WebhookTargetRequest,
} from '@xver/apis/v1/sammeln/webhooks/model';
import Button from '@xver/components/Atoms/Button/Button';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WEBHOOKS_QUERY_KEY } from '../hooks/useWebhooksQuery';

interface AddWebhookModalProps {
    opened: boolean;
    onClose: () => void;
    projectIdOrName: string;
}

interface TargetRow {
    type: string;
    address: string;
    auth_header: string;
    skip_cert_verify: boolean;
    payload_format: string;
}

function createEmptyTarget(): TargetRow {
    return {
        type: '',
        address: '',
        auth_header: '',
        skip_cert_verify: false,
        payload_format: '',
    };
}

export default function AddWebhookModal({
    opened,
    onClose,
    projectIdOrName,
}: AddWebhookModalProps) {
    const { t } = useTranslation('artifactRegistry');
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [eventTypes, setEventTypes] = useState<string[]>([]);
    const [targets, setTargets] = useState<TargetRow[]>([createEmptyTarget()]);
    const [showErrors, setShowErrors] = useState(false);
    const formId = useId();
    const nameId = `${formId}-name`;
    const eventsId = `${formId}-events`;
    const eventsErrorId = `${eventsId}-error`;
    const targetFieldId = (index: number, field: 'type' | 'address') =>
        `${formId}-target-${index}-${field}`;

    useEffect(() => {
        if (!opened) return;
        setName('');
        setDescription('');
        setEnabled(true);
        setEventTypes([]);
        setTargets([createEmptyTarget()]);
        setShowErrors(false);
    }, [opened]);

    const supportedQuery = useQuery<SupportedEventTypesResponse>({
        queryKey: ['apis.v1.sammeln.webhooks.events', projectIdOrName],
        queryFn: () => Endpoints.v1.Sammeln.Webhooks.getSupportedEventTypes(projectIdOrName),
        enabled: opened && !!projectIdOrName,
    });

    const supportedEvents = supportedQuery.data?.event_types ?? [];
    const notifyTypes = supportedQuery.data?.notify_types ?? [];
    const payloadFormats = supportedQuery.data?.payload_formats ?? [];

    const eventTypeOptions = useMemo(
        () => supportedEvents.map((e) => ({ value: e, label: e })),
        [supportedEvents]
    );
    const notifyTypeOptions = useMemo(
        () => notifyTypes.map((n) => ({ value: n, label: n })),
        [notifyTypes]
    );

    const createMutation = useMutation({
        mutationFn: () => {
            const payload: WebhookTargetRequest[] = targets.map((tg) => ({
                type: tg.type,
                address: tg.address,
                auth_header: tg.auth_header || undefined,
                skip_cert_verify: tg.skip_cert_verify,
                payload_format: tg.payload_format || undefined,
            }));
            return Endpoints.v1.Sammeln.Webhooks.createWebhook(projectIdOrName, {
                name,
                description,
                targets: payload,
                event_types: eventTypes,
                enabled,
            });
        },
        onSuccess: async () => {
            notifications.show({
                title: t('webhooks.toast.createTitle', 'Webhook created'),
                message: t('webhooks.toast.createMessage', 'Your webhook is now active.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({
                queryKey: [WEBHOOKS_QUERY_KEY, projectIdOrName],
            });
            onClose();
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('webhooks.toast.errorTitle', 'Action failed'),
                message:
                    err?.message ?? t('webhooks.toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const isValid =
        name.trim().length > 0 &&
        eventTypes.length > 0 &&
        targets.length > 0 &&
        targets.every((tg) => tg.type && tg.address);

    const nameError =
        showErrors && !name.trim()
            ? t('webhooks.modal.nameRequired', 'Enter a webhook name.')
            : undefined;
    const eventsError =
        showErrors && eventTypes.length === 0
            ? t('webhooks.modal.eventsRequired', 'Select at least one event type.')
            : undefined;
    const targetTypeError = t('webhooks.modal.targetTypeRequired', 'Select a notification type.');
    const targetAddressError = t('webhooks.modal.targetAddressRequired', 'Enter an address.');

    const firstInvalidId = () => {
        if (!name.trim()) return nameId;
        if (eventTypes.length === 0) return eventsId;
        const index = targets.findIndex((tg) => !tg.type || !tg.address);
        if (index === -1) return undefined;
        return targetFieldId(index, targets[index].type ? 'address' : 'type');
    };

    const handleSubmit = () => {
        if (createMutation.isPending) return;
        if (!isValid) {
            setShowErrors(true);
            const id = firstInvalidId();
            if (id) document.getElementById(id)?.focus();
            return;
        }
        createMutation.mutate();
    };

    const updateTarget = (index: number, patch: Partial<TargetRow>) => {
        setTargets((prev) => prev.map((tg, i) => (i === index ? { ...tg, ...patch } : tg)));
    };

    const addTarget = () => setTargets((prev) => [...prev, createEmptyTarget()]);
    const removeTarget = (index: number) =>
        setTargets((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

    return (
        <Modal
            size="lg"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {t('webhooks.modal.title', 'Create webhook')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {t(
                            'webhooks.modal.description',
                            'Send registry event notifications to one or more endpoints.'
                        )}
                    </p>
                </div>

                {supportedQuery.isPending ? (
                    <div className="w-full flex justify-center py-6">
                        <Loader size={20} color="var(--fg)" />
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        <TextInput
                            id={nameId}
                            label={t('webhooks.modal.name', 'Name')}
                            value={name}
                            onChange={(e) => setName(e.currentTarget.value)}
                            error={nameError}
                            required
                            placeholder={t('webhooks.modal.namePlaceholder', 'e.g. slack-notifier')}
                        />
                        <Textarea
                            label={t('webhooks.modal.descriptionLabel', 'Description')}
                            value={description}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                            placeholder={t(
                                'webhooks.modal.descriptionPlaceholder',
                                'Optional description'
                            )}
                            autosize
                            minRows={2}
                        />
                        <Switch
                            label={t('webhooks.modal.enabled', 'Enabled')}
                            checked={enabled}
                            onChange={(e) => setEnabled(e.currentTarget.checked)}
                        />
                        <fieldset
                            id={eventsId}
                            tabIndex={-1}
                            aria-describedby={eventsError ? eventsErrorId : undefined}
                            className="flex flex-col gap-2 rounded-sm outline-offset-4"
                        >
                            <legend className="text-sm font-medium mb-2">
                                {t('webhooks.modal.events', 'Event types')}
                                <span
                                    aria-hidden="true"
                                    style={{ color: 'var(--mantine-color-error)' }}
                                >
                                    {' *'}
                                </span>
                            </legend>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {eventTypeOptions.map((option) => (
                                    <Checkbox
                                        key={option.value}
                                        value={option.value}
                                        label={option.label}
                                        checked={eventTypes.includes(option.value)}
                                        onChange={(e) => {
                                            const { checked } = e.currentTarget;
                                            setEventTypes((prev) =>
                                                checked
                                                    ? [...prev, option.value]
                                                    : prev.filter((v) => v !== option.value)
                                            );
                                        }}
                                    />
                                ))}
                            </div>
                            {eventsError && (
                                <Input.Error id={eventsErrorId}>{eventsError}</Input.Error>
                            )}
                        </fieldset>

                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row justify-between items-center">
                                <span className="text-sm font-medium">
                                    {t('webhooks.modal.targets', 'Targets')}
                                </span>
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    leftIcon={<IconPlus size={14} />}
                                    onClick={addTarget}
                                >
                                    {t('webhooks.modal.addTarget', 'Add target')}
                                </Button>
                            </div>
                            <div className="flex flex-col gap-3">
                                {targets.map((tg, index) => {
                                    const formats =
                                        payloadFormats.find((pf) => pf.notify_type === tg.type)
                                            ?.formats ?? [];
                                    return (
                                        <div
                                            key={index}
                                            className="p-3 flex flex-col gap-3 border-[0.5px] border-line rounded-md bg-surface-2"
                                        >
                                            <div className="flex flex-row justify-between items-center">
                                                <span className="text-xs text-fg/68">
                                                    {t('webhooks.modal.targetLabel', {
                                                        index: index + 1,
                                                        defaultValue: 'Target #{{index}}',
                                                    })}
                                                </span>
                                                {targets.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTarget(index)}
                                                        className="text-fg/68 hover:text-red-400 light:hover:text-red-700 transition-colors"
                                                        aria-label={t(
                                                            'webhooks.modal.removeTarget',
                                                            'Remove target'
                                                        )}
                                                    >
                                                        <IconTrash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <Select
                                                id={targetFieldId(index, 'type')}
                                                label={t('webhooks.modal.targetType', 'Type')}
                                                data={notifyTypeOptions}
                                                value={tg.type || null}
                                                onChange={(value) =>
                                                    updateTarget(index, {
                                                        type: value ?? '',
                                                        payload_format: '',
                                                    })
                                                }
                                                placeholder={t(
                                                    'webhooks.modal.targetTypePlaceholder',
                                                    'Select notification type'
                                                )}
                                                error={
                                                    showErrors && !tg.type
                                                        ? targetTypeError
                                                        : undefined
                                                }
                                                required
                                            />
                                            <TextInput
                                                id={targetFieldId(index, 'address')}
                                                label={t(
                                                    'webhooks.modal.targetAddress',
                                                    'Address / URL'
                                                )}
                                                value={tg.address}
                                                onChange={(e) =>
                                                    updateTarget(index, {
                                                        address: e.currentTarget.value,
                                                    })
                                                }
                                                placeholder="https://example.com/webhook"
                                                error={
                                                    showErrors && !tg.address
                                                        ? targetAddressError
                                                        : undefined
                                                }
                                                required
                                            />
                                            <TextInput
                                                label={t(
                                                    'webhooks.modal.targetAuth',
                                                    'Authorization header'
                                                )}
                                                value={tg.auth_header}
                                                onChange={(e) =>
                                                    updateTarget(index, {
                                                        auth_header: e.currentTarget.value,
                                                    })
                                                }
                                                placeholder={t(
                                                    'webhooks.modal.targetAuthPlaceholder',
                                                    'Optional bearer token or header value'
                                                )}
                                            />
                                            {formats.length > 0 && (
                                                <Select
                                                    label={t(
                                                        'webhooks.modal.payloadFormat',
                                                        'Payload format'
                                                    )}
                                                    data={formats.map((f) => ({
                                                        value: f,
                                                        label: f,
                                                    }))}
                                                    value={tg.payload_format || null}
                                                    onChange={(value) =>
                                                        updateTarget(index, {
                                                            payload_format: value ?? '',
                                                        })
                                                    }
                                                    placeholder={t(
                                                        'webhooks.modal.payloadFormatPlaceholder',
                                                        'Select payload format'
                                                    )}
                                                    clearable
                                                />
                                            )}
                                            <Switch
                                                label={t(
                                                    'webhooks.modal.skipCert',
                                                    'Skip certificate verification'
                                                )}
                                                checked={tg.skip_cert_verify}
                                                onChange={(e) =>
                                                    updateTarget(index, {
                                                        skip_cert_verify: e.currentTarget.checked,
                                                    })
                                                }
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <div className="w-full flex flex-row justify-end items-center gap-2 pt-2 border-t-[0.5px] border-line">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        disabled={createMutation.isPending}
                    >
                        {t('webhooks.modal.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={createMutation.isPending}
                        isLoading={createMutation.isPending}
                    >
                        {t('webhooks.modal.submit', 'Create webhook')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
